/*
 * The search card, shared by the browse page and the release-note list.
 *
 * The owner asked for release-note search "similar to articles search" (S43,
 * 2026-10-13), and the screenshots showed the browse page's search card above
 * its PRODUCT box. So it is the same card rather than a look-alike - extracted
 * from BrowsePage with its markup unchanged, as ProductFilter.tsx was for S42:
 * a tight card holding a visually hidden label and a `type="search"` box with
 * the placeholder "Search…". The browse page renders exactly what it rendered
 * before, and a change to the card lands on both pages at once. The label is the
 * one part that differs ("Search articles", "Search release notes"), because it
 * is what a screen reader announces as the box's name and it should say what
 * the box searches.
 *
 * The behaviour moved with it, into useUrlSearch below, because the card without
 * its behaviour is the half that cannot drift. The rules are the browse page's:
 * the box answers every keystroke at once, the query string's `?q=` is written
 * per SETTLED value (350 ms after the last keystroke, so "h-he-hel-hell-hello"
 * is one Back stop, not five) and that write resets the page, and a `?q=` the
 * page did not write itself - back, forward, a link into a search - is adopted
 * into the box.
 *
 * One thing changed in the move, and on both pages: the list is fetched for the
 * query string's q rather than for the debounced box value. While the reader
 * types the two are the same value one render apart. They differ when the
 * address changes from outside - Back, or a "Clear filters" that also clears
 * the search - and there the debounced value trailed the address by 350 ms,
 * fetching a list for neither the old address nor the new one in between (the
 * products already cleared, the search not yet). The same was true of a settled
 * search on page 3: one fetch for the new words on the OLD page, then the page
 * reset and a second. Keyed on the address, each is one fetch, for exactly what
 * the address says - which is what the browse page's own comment always claimed:
 * "what the URL says and what the list shows can never disagree".
 */

import { useEffect, useRef, useState } from 'react';
import { useMessages } from '../i18n/language.js';

/** How long the box waits after the last keystroke before the search is written to `?q=`. */
export const SEARCH_DEBOUNCE_MS = 350;

/** The search box's state, as useUrlSearch hands it to a page. */
export interface UrlSearch {
  /** What the box shows: every keystroke, at once. */
  draft: string;
  /** The box's onChange. */
  setDraft: (value: string) => void;
  /**
   * What the page searches for: the query string's `q`, as written. Untrimmed -
   * the server trims it and echoes the result, and the echo is what a page should
   * PRINT; this is what it should FETCH with.
   */
  search: string;
}

/**
 * The search box's behaviour: the draft, the debounced write to `?q=`, and the
 * adoption of a `?q=` written by someone else.
 *
 * `urlSearch` is the query string's current `q` ('' when absent). `write` puts a
 * settled value in the query string - '' means remove it - and is expected to
 * reset the page, since a new search is a new list.
 */
export function useUrlSearch(urlSearch: string, write: (settled: string) => void): UrlSearch {
  const [draft, setDraft] = useState(urlSearch);

  // The last q value this hook itself put in (or adopted from) the URL. It is
  // what tells an echo of its own write apart from an external change.
  const lastWritten = useRef(urlSearch);

  // The latest write, read when the timer fires: a product ticked during the
  // 350 ms must not be undone by the query string an older render closed over.
  const writeRef = useRef(write);
  useEffect(() => {
    writeRef.current = write;
  });

  // The draft reaches the query string 350 ms after the last keystroke.
  //
  // A timer of its own, keyed on the draft, and not useDebounced's settled value
  // keyed on a CHANGE of that value: after an outside change (Clear, Back) the
  // debounced value still held the old search, so retyping exactly those words
  // within 350 ms never changed it, nothing was written, and the box and the list
  // disagreed until the next keystroke (S43 review, 2026-10-13). The second check
  // inside the timer is for the reverse: an outside change that lands on the
  // pending value itself must not be written a second time.
  useEffect(() => {
    if (draft === lastWritten.current) return;
    const timer = setTimeout(() => {
      if (draft === lastWritten.current) return;
      lastWritten.current = draft;
      writeRef.current(draft);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  // The URL changed and it was not this hook writing it - back/forward, a link
  // into a search, a Clear button. Adopt it, or the write-effect above would
  // undo the navigation moments later.
  useEffect(() => {
    if (urlSearch === lastWritten.current) return;
    lastWritten.current = urlSearch;
    setDraft(urlSearch);
  }, [urlSearch]);

  return { draft, setDraft, search: urlSearch };
}

/**
 * The search card: a tight sidebar card with a visually hidden label and the
 * search box. `label` names the box for a screen reader - say what it searches.
 */
export function SearchFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const t = useMessages();

  return (
    <div className="card card-tight">
      <label htmlFor="search" className="visually-hidden">
        {label}
      </label>
      <input
        id="search"
        type="search"
        placeholder={t.filters.searchPlaceholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
